import { DomHandler } from 'primeng/dom';

/**
 * PrimeNG 19 `DomHandler.absolutePosition` — makes body-appended overlays open
 * UPWARD when there is no room below the trigger. Applied app-wide from
 * main.ts, same approach as primeng-sidebar-esc.patch. Remove if PrimeNG fixes
 * the upstream measurement.
 *
 * Every paginator on the app is `[paginatorDropdownAppendTo]="'body'"` (the
 * rows-per-page panel would otherwise be clipped by `.card { overflow: hidden }`),
 * and the paginator always sits at the BOTTOM of its table — so the panel
 * routinely opens off the bottom edge of the window and the last options
 * (50 / 100 / All) are unreachable. Same for any `appendTo="body"` p-select,
 * p-multiselect or p-datepicker placed low on a page.
 *
 * PrimeNG does have a flip: `absolutePosition` compares
 * `trigger bottom + overlay height` against the viewport height. But the height
 * it uses is the overlay ANCHOR's `offsetHeight`, read from inside the content
 * animation's start callback — and there it reports 0 (first documented on the
 * Extractor Name field fix in add-modelconfig). `bottom + 0 > viewport` is never
 * true, so the panel always opens downward however little room is left.
 *
 * Fix: same placement as upstream, but the height is the largest of the anchor's
 * offsetHeight, its scrollHeight (which includes an absolutely positioned child),
 * and each direct child's own extent. Upstream's other fallback is kept: no room
 * below AND no room above pins the panel to the top of the viewport. The gutter
 * margin is also applied per side as intended — upstream reads an undeclared
 * `origin` (which resolves to `window.origin`) and so always applies the
 * downward gutter, nudging an upward-opened panel 2px onto its trigger.
 */
function measureOverlayHeight(element: HTMLElement): number {
  let height = Math.max(element.offsetHeight, element.scrollHeight);
  for (const child of Array.from(element.children) as HTMLElement[]) {
    height = Math.max(height, child.offsetHeight, child.scrollHeight);
  }
  return height;
}

(DomHandler as any).absolutePosition = function (element: HTMLElement, target: HTMLElement, gutter = true): void {
  const hidden = !element.offsetParent;
  const hiddenDims = hidden ? DomHandler.getHiddenElementDimensions(element) : null;
  const elementOuterHeight = hidden ? hiddenDims.height : measureOverlayHeight(element);
  const elementOuterWidth = hidden ? hiddenDims.width : element.offsetWidth;
  const targetOuterHeight = target.offsetHeight;
  const targetOuterWidth = target.offsetWidth;
  const targetOffset = target.getBoundingClientRect();
  const windowScrollTop = DomHandler.getWindowScrollTop();
  const windowScrollLeft = DomHandler.getWindowScrollLeft();
  const viewport = DomHandler.getViewport();
  let top: number;
  let left: number;
  let origin: 'top' | 'bottom';

  if (targetOffset.top + targetOuterHeight + elementOuterHeight > viewport.height) {
    // No room below: open upward, panel bottom touching the trigger top.
    top = targetOffset.top + windowScrollTop - elementOuterHeight;
    origin = 'bottom';
    if (top < 0) {
      top = windowScrollTop;
    }
  } else {
    top = targetOuterHeight + targetOffset.top + windowScrollTop;
    origin = 'top';
  }
  element.style.transformOrigin = origin;

  if (targetOffset.left + elementOuterWidth > viewport.width) {
    left = Math.max(0, targetOffset.left + windowScrollLeft + targetOuterWidth - elementOuterWidth);
  } else {
    left = targetOffset.left + windowScrollLeft;
  }

  element.style.top = top + 'px';
  element.style.left = left + 'px';
  if (gutter) {
    element.style.marginTop = origin === 'bottom' ? 'calc(var(--p-anchor-gutter) * -1)' : 'calc(var(--p-anchor-gutter))';
  }
};
